/* ==========================================================================
   hp-usp-card.js

   Behaviour for <hp-usp-card>. Ported from the UK page's v3 USP script, with
   the timer bookkeeping rewritten: the original kept a module-level registry
   keyed by section id and cleared it on every re-render, because it ran as an
   inline script in the section. As a custom element, each instance owns its
   own timer and disconnectedCallback clears it, so the editor cannot leave
   orphaned intervals running.
   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('hp-usp-card')) return;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  customElements.define('hp-usp-card', class HpUspCard extends HTMLElement {
    connectedCallback() {
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-hp-usp-panel]'));
      this.tabs   = Array.prototype.slice.call(this.querySelectorAll('[data-hp-usp-tab]'));
      this.count  = parseInt(this.getAttribute('data-count'), 10) || this.panels.length;
      if (this.count < 1) return;

      this.timer = null;
      this.stopped = false;

      var self = this;

      this.tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          /*  A click is a deliberate choice, so rotation stops for good
              rather than yanking the panel away a second later. */
          self.show(parseInt(tab.getAttribute('data-hp-usp-tab'), 10), true);
        });
      });

      /*  Learn more opens a modal. Delegated so panels can be re-rendered. */
      this.addEventListener('click', function (event) {
        var trigger = event.target.closest('[data-hp-usp-open]');
        if (!trigger) return;
        event.preventDefault();
        self.openModal(trigger.getAttribute('data-hp-usp-open'));
      });

      this.addEventListener('mouseenter', function () { self.pause(); });
      this.addEventListener('mouseleave', function () { self.start(); });
      this.addEventListener('focusin', function () { self.pause(); });

      /*  Off-screen and background-tab rotation is wasted work, and coming
          back to a card mid-transition looks broken. */
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { e.isIntersecting ? self.start() : self.pause(); });
        }, { threshold: 0.2 });
        this.io.observe(this);
      }

      this.onVisibility = function () {
        document.hidden ? self.pause() : self.start();
      };
      document.addEventListener('visibilitychange', this.onVisibility);

      this.moveModals();
      this.setArrow(0);
      this.start();
    }

    disconnectedCallback() {
      this.pause();
      if (this.io) this.io.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    /*  A `position: sticky` ancestor establishes a containing block, so a
        fixed-position modal inside the info column cannot escape it and no
        z-index helps. Reparenting to <body> is the only fix. */
    moveModals() {
      var id = this.id;
      if (!id) return;
      var prefix = id.replace('HpUspCard-', 'HpUspModal-');
      var modals = document.querySelectorAll('[id^="' + prefix + '"]');
      Array.prototype.forEach.call(modals, function (m) {
        if (m.parentElement !== document.body) document.body.appendChild(m);
      });
    }

    setArrow(index) {
      this.style.setProperty('--uc-arrow', ((100 / this.count) * (index + 0.5)) + '%');
    }

    show(index, fromUser) {
      index = ((index % this.count) + this.count) % this.count;
      this.setAttribute('data-active', index);
      this.setArrow(index);

      this.panels.forEach(function (p) {
        var on = parseInt(p.getAttribute('data-hp-usp-panel'), 10) === index;
        p.classList.toggle('is-active', on);
        on ? p.removeAttribute('aria-hidden') : p.setAttribute('aria-hidden', 'true');
      });

      this.tabs.forEach(function (t) {
        var on = parseInt(t.getAttribute('data-hp-usp-tab'), 10) === index;
        t.classList.toggle('is-active', on);
        if (t.getAttribute('role') === 'tab') t.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      if (fromUser) this.stop();
    }

    start() {
      if (this.stopped || this.timer || this.count < 2 || REDUCED) return;
      if (this.getAttribute('data-autoplay') !== 'true') return;
      if (document.hidden) return;
      var self = this;
      var ms = parseInt(this.getAttribute('data-interval'), 10) || 3000;
      this.timer = window.setInterval(function () {
        self.show((parseInt(self.getAttribute('data-active'), 10) || 0) + 1);
      }, ms);
    }

    pause() {
      if (this.timer) { window.clearInterval(this.timer); this.timer = null; }
    }

    stop() {
      this.stopped = true;
      this.pause();
    }

    openModal(id) {
      var modal = document.getElementById(id);
      if (!modal) return;
      modal.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';

      var card = modal.querySelector('.hp-uspcard-modal__card');
      if (card) card.focus();

      var close = function () {
        modal.classList.remove('is-open');
        document.documentElement.style.overflow = '';
        document.removeEventListener('keydown', onKey);
      };
      var onKey = function (e) { if (e.key === 'Escape') close(); };

      modal.addEventListener('click', function (e) {
        /*  Backdrop click closes; a click inside the card does not. */
        if (e.target === modal || e.target.closest('[data-hp-usp-close]')) close();
      });
      document.addEventListener('keydown', onKey);
    }
  });
})();
